import React, { useLayoutEffect, useEffect, useState, useMemo } from 'react';
import { Router, useLocation, navigate } from '@reach/router';

/**
 * MUI
 */
import { Container, Box, makeStyles, Theme } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

/**
 * Hooks
 */
import { useInit, useUser, useLibraryNotification } from 'hooks';

import { AppLayoutProps } from './type';

import { RouterPage } from 'App';
import { Header } from 'pages';

import { MainHeaderBackground, MainHeaderBackgroundMobile } from 'asset/images';

/**
 * Components
 */
import { PageLoading } from 'components/Loading';
import { LibraryModal } from 'components/Modal';
import ErrorModal from 'components/Modal/ErrorModal';
import Notifier from 'components/Notifier/Notifier';
import { Footer } from 'components/Footer';
import { ScrollToTop } from 'components/Scroll';
import {
  SearchResultByDate,
  SearchResultByRecord,
  SearchResultBySpecial,
} from 'components/SearchResult';
import NotificationDialog from 'components/NotificationDialog';
import useCheckNotificationDialog from 'hooks/useCheckNotificationDialog';

const useStyles = makeStyles((theme: Theme) => ({
  headerContainer: {
    width: '100%',
    minHeight: '520px',
    backgroundColor: `rgba(1, 8, 22, 1.0)`,
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      minWidth: '320px',
      minHeight: 'auto',
    },
  },
  contentHeader: {
    width: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '0 0 32px 0',
    backgroundColor: `rgba(1, 8, 22, 1.0)`,
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      minWidth: '320px',
      padding: '0 0 12px 0',
    },
  },
  contentPreviewHeader: {
    padding: '0 0 32px 0',
    backgroundColor: `rgba(1, 8, 22, 1.0)`,
    maxWidth: '2240px',
    margin: '0 auto',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      minWidth: '320px',
      padding: '0 0 12px 0',
    },
  },
  contentsBackground: {
    width: '100%', // 1200px to fixed size
    height: '520px',
    left: 0,
    right: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'absolute',
    zIndex: 1,
    background: `center top / 90% no-repeat url(${MainHeaderBackground})`,
    backgroundSize: 'cover',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      height: '32vw',
      minWidth: '320px',
      minHeight: '116px',
      background: `center top / 90% no-repeat url(${MainHeaderBackgroundMobile})`,
      backgroundSize: 'cover',
    },
  },
  contentHeaderChild: {
    // maxWidth: '1200px',
    margin: 'auto',
    zIndex: 20,
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      paddingLeft: '15px',
      paddingRight: '15px',
    },
  },
  contentsContainer: {
    maxWidth: '1200px',
    margin: 'auto',
    zIndex: 20,
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      paddingLeft: '15px',
      paddingRight: '15px',
    },
  },
  scrollToTop: {
    width: '50px',
    height: '50px',
    borderRadius: '9999px',
    position: 'fixed',
    bottom: '40px',
    right: '40px',
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    color: 'black',
    cursor: 'pointer',
    opacity: '0.7',
    boxShadow: '2px 2px 8px rgb(0 0 0 / 0.5)',
    [theme.breakpoints.down('xs')]: {
      width: '40px',
      height: '40px',
      bottom: '20px',
      right: '20px',
    },
  },
}));

function AppLayout({ children }: AppLayoutProps) {
  const classes = useStyles();
  const { onInit, isInit } = useInit();
  const { onAuthenticate } = useUser();
  const location = useLocation();
  const [checkNotification, setCheckNotification] = useCheckNotificationDialog();
  const [openNotificationDialog, setOpenNotificationDialog] = useState(!checkNotification);

  const isPreviewPage = useMemo(() => {
    return location.pathname.startsWith('/preview');
  }, [location]);

  useLibraryNotification();

  useEffect(() => {
    const { pathname, search } = location;
    const redirectUrl = pathname + search;

    onAuthenticate(redirectUrl);
    onInit();
  }, [onAuthenticate, onInit]);

  return (
    <Container maxWidth={false} disableGutters>
      <PageLoading />
      <ErrorModal />
      <LibraryModal />
      <Notifier />
      <ScrollToTop />

      <Container className={classes.headerContainer} maxWidth={false} disableGutters>
        <Box className={classes.contentsBackground} />

        <Box className={isPreviewPage ? classes.contentPreviewHeader : classes.contentHeader}>
          <Header />
          <div className={classes.contentHeaderChild}>{isInit && children}</div>
        </Box>
      </Container>
      <Container className={classes.contentsContainer} disableGutters>
        <Router location={location}>
          <RouterPage path="search/date" pageComponent={<SearchResultByDate />} />
          <RouterPage path="search/record" pageComponent={<SearchResultByRecord />} />
          <RouterPage path="search/special" pageComponent={<SearchResultBySpecial />} />
        </Router>
      </Container>
      <Footer />
      <NotificationDialog
        openState={[openNotificationDialog, setOpenNotificationDialog]}
        checkNotificationState={[checkNotification, setCheckNotification]}
      />
      {!isPreviewPage && (
        <div
          className={classes.scrollToTop}
          onClick={() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }}
        >
          <ArrowUpwardIcon />
        </div>
      )}
    </Container>
  );
}

export default AppLayout;
